import { useCallback } from "react";
import {
  magicMockApiV1UnifiedUpdateProfilePut,
  magicMockApiV1UnifiedProfilesPost,
  useGenerateProfileTokenApiV1UnifiedGenerateProfileTokenPost,
  studentsControllerGetEnrolledPrograms,
  LiveclassStudent,
  generateProfileTokenApiV1UnifiedGenerateProfileTokenPost,
} from "@oneapp/data";
import { emitPostAuthMarketingLsq } from "@repo/analytics";
import {
  useAuthStore,
  type EnrolledProgram,
  type ProfilesData,
  type ProfileItem,
} from "../../store/useAuthStore";
import { queryClient } from "../../lib/queryClient";
import { useNavigate } from "react-router-dom";
import { ROUTESWEB } from "@repo/utils";

const getGatewayHeaders = (authorization: string) => {
  const token = authorization.replace(/^Bearer\s+/i, "").trim();
  return {
    Authorization: authorization,
    accesstoken: token,
    platform: "web",
  };
};

export interface UpdateProfilePayload {
  uam_id?: string;
  grade_id?: number;
  exams?: number[];
  exam_ids?: number[];
  vertical_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  guardian_name?: string;
  guardian_phone?: string;
  is_default_profile?: boolean;
  profile_photo?: string | null;
  skipLsqMarketing?: boolean;
}

export type ProfileUpdaterOptions = {
  /** When false, caller owns {@link useAuthStore} `isSyncing` (e.g. deeplink handler). */
  manageSyncing?: boolean;
};

const filterUndefinedValues = <T extends Record<string, unknown>>(
  payload: T,
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
};

export const useprofileUpdater = () => {
  const navigate = useNavigate();
  const generateProfileTokenMutation =
    useGenerateProfileTokenApiV1UnifiedGenerateProfileTokenPost({
      request: { clientName: "learnGateway" },
    });

  // const [showContactSupportModal, setShowContactSupportModal] = useState(false);

  const profileUpdater = useCallback(
    async (
      payload: UpdateProfilePayload,
      options?: ProfileUpdaterOptions,
    ): Promise<{
      shouldShowContactSupportModal: boolean;
      navigatedToSignupUpdateFlow: boolean;
    }> => {
      const manageSyncing = options?.manageSyncing !== false;
      const authState = useAuthStore.getState();
      const { loginMethod } = authState;
      const currentToken =
        authState.token ?? authState.transientAccessToken ?? undefined;
      const profilesData = authState.profilesData;
      let shouldShowContactSupportModal = false;
      let navigatedToSignupUpdateFlow = false;
      // setShowContactSupportModal(false);

      // Set syncing flag to prevent logout during profile sync
      if (manageSyncing) {
        authState.setIsSyncing(true);
      }

      if (!currentToken) {
        throw new Error("No authentication token found. Please login again.");
      }

      if (!profilesData?.unified_account_details?.unified_account_id) {
        throw new Error("No profile data found. Please login again.");
      }

      const unifiedAccountId =
        profilesData.unified_account_details.unified_account_id;
      const skipLsqMarketing = payload.skipLsqMarketing === true;

      const profilesListInitial =
        profilesData.unified_account_details.profiles ?? [];
      if (
        profilesListInitial.length === 0 &&
        (payload.uam_id == null || String(payload.uam_id).trim() === "")
      ) {
        throw new Error("No profile ID found. Please login again.");
      }

      const filteredPayload = filterUndefinedValues({
        uam_id: payload.uam_id,
        ...(payload.grade_id !== undefined && { grade_id: payload.grade_id }),
        ...(payload.exams !== undefined && { exams: payload.exams }),
        ...(payload.exam_ids !== undefined && { exam_ids: payload.exam_ids }),
        ...(payload.vertical_id !== undefined && {
          vertical_id: payload.vertical_id,
        }),
        ...(payload.is_default_profile !== undefined && {
          is_default_profile: payload.is_default_profile,
        }),
        ...(payload.first_name !== undefined && {
          first_name: payload.first_name,
        }),
        ...(payload.last_name !== undefined && {
          last_name: payload.last_name,
        }),
        ...(payload.email !== undefined && { email: payload.email }),
        ...(payload.guardian_name !== undefined && {
          guardian_name: payload.guardian_name,
        }),
        ...(payload.guardian_phone !== undefined && {
          guardian_phone: payload.guardian_phone,
        }),
        ...(payload.profile_photo !== undefined && {
          profile_photo: payload.profile_photo,
        }),
      });

      let updateProfileResponse:
        | { grade?: number; exam_ids?: number[] }
        | undefined;

      let defaultProfile: ProfileItem;

      try {
        updateProfileResponse = (await magicMockApiV1UnifiedUpdateProfilePut(
          filteredPayload as Parameters<
            typeof magicMockApiV1UnifiedUpdateProfilePut
          >[0],
          {
            clientName: "learnGateway",
            headers: getGatewayHeaders(`Bearer ${currentToken}`),
          },
        )) as { grade?: number; exam_ids?: number[] };
      } catch (updateError) {
        console.warn("[ProfileUpdate] Failed to update profile:", updateError);
        if (manageSyncing) {
          authState.setIsSyncing(false);
        }
        throw updateError;
      }

      let profilesAccessToken: string | undefined;
      let refreshedProfilesData: ProfilesData | undefined;
      try {
        const profilesResponse = await magicMockApiV1UnifiedProfilesPost(
          { unified_account_id: unifiedAccountId } as unknown as Parameters<
            typeof magicMockApiV1UnifiedProfilesPost
          >[0],
          {
            clientName: "learnGateway",
            headers: getGatewayHeaders(`Bearer ${currentToken}`),
          },
        );

        refreshedProfilesData = profilesResponse as ProfilesData;
        profilesAccessToken = refreshedProfilesData.access_token;

        const allProfiles =
          refreshedProfilesData.unified_account_details.profiles ?? [];

        defaultProfile =
          loginMethod !== "admission_id"
            ? (allProfiles?.find((p) => p.is_default_profile) ??
              allProfiles?.find((p) => p.vertical_id === 1) ??
              allProfiles?.[0])
            : (allProfiles?.find((p) => p.is_default_profile) ??
              allProfiles?.find((p) => p.vertical_id !== 1) ??
              allProfiles?.[0]);

        const normalizedProfilesData: ProfilesData = {
          ...refreshedProfilesData,
          unified_account_details: {
            ...refreshedProfilesData.unified_account_details,
            profiles: [
              defaultProfile,
              ...(refreshedProfilesData.unified_account_details.profiles?.filter(
                (p) => p !== defaultProfile,
              ) ?? []),
            ],
          },
        };

        authState.setProfilesData(normalizedProfilesData);

        authState.setUnifiedAccountDetails(
          refreshedProfilesData.unified_account_details,
        );
      } catch (profilesError) {
        console.warn("[ProfileUpdate] Failed to get profiles:", profilesError);
        profilesAccessToken = currentToken;
      }

      if (!profilesAccessToken) {
        throw new Error("Failed to get profile access token");
      }

      const uamIdForGenerateToken: number | null = (() => {
        if (payload.uam_id != null && String(payload.uam_id).trim() !== "") {
          const n = Number(String(payload.uam_id).trim());
          if (Number.isFinite(n) && n > 0) return n;
        }
        const list =
          refreshedProfilesData?.unified_account_details?.profiles ??
          profilesListInitial;
        const raw =
          list.find((p) => p.is_default_profile)?.uam_id ??
          list.find((p) => p.vertical_id === 1)?.uam_id ??
          list[0]?.uam_id;
        if (raw == null) return null;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
      })();

      if (uamIdForGenerateToken == null) {
        throw new Error("No profile ID found. Please login again.");
      }

      let genData: { access_token?: string; refresh_token?: string };
      try {
        genData = (await generateProfileTokenMutation.mutateAsync({
          data: {
            unified_account_id: unifiedAccountId,
            uam_id: uamIdForGenerateToken,
          },
          request: {
            headers: getGatewayHeaders(`Bearer ${profilesAccessToken}`),
          },
        })) as { access_token?: string; refresh_token?: string };
      } catch (tokenError) {
        console.warn(
          "[ProfileUpdate] Failed to generate profile token:",
          tokenError,
        );
        throw tokenError;
      }

      if (!genData.access_token) {
        throw new Error("Failed to generate access token");
      }
      const accessToken = genData.access_token;
      const refreshToken = genData.refresh_token;
      const enrolledProgramsHeaders = {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
      };

      const resolvedGradeId = payload.grade_id ?? updateProfileResponse?.grade;
      const resolvedExamIds =
        payload.exam_ids !== undefined
          ? payload.exam_ids
          : updateProfileResponse?.exam_ids;

      if (resolvedGradeId !== undefined) {
        let programs: EnrolledProgram[] | undefined;
        const examsParam =
          Array.isArray(resolvedExamIds) && resolvedExamIds.length > 0
            ? resolvedExamIds.join(",")
            : "";
        try {
          const enrolledResponse = (await studentsControllerGetEnrolledPrograms(
            {
              grade: resolvedGradeId,
              processEnrollment: true,
              ...(examsParam ? { exams: examsParam } : {}),
            },
            { clientName: "oneApp", headers: enrolledProgramsHeaders },
          )) as { data?: { programs?: EnrolledProgram[] } } | undefined;

          programs = enrolledResponse?.data?.programs;
          if (programs && Array.isArray(programs) && programs.length > 0) {
            authState.setEnrolledPrograms(programs);
          } else {
            const plist =
              useAuthStore.getState().profilesData?.unified_account_details
                ?.profiles ?? [];
            const switchedUamId =
              payload.uam_id != null ? String(payload.uam_id) : undefined;
            const activeProfile =
              plist.find(
                (p) =>
                  switchedUamId != null && String(p.uam_id) === switchedUamId,
              ) ??
              plist.find((p) => p.is_default_profile) ??
              plist[0];
            const verticalId = activeProfile?.vertical_id;

            const uad =
              profilesData?.unified_account_details?.user_account_details;
            const phoneNumber = String(uad?.phone ?? "");
            const isd_code = String(uad?.isd_code ?? "+91");
            const whatsapp_consent = Boolean(uad?.whatsapp_consent ?? true);

            if (verticalId === 1) {
              navigatedToSignupUpdateFlow = true;
              navigate(`/${ROUTESWEB.AUTH.SIGNUP}`, {
                replace: true,
                state: {
                  phoneNumber,
                  isd_code,
                  whatsapp_consent,
                  isUpdateFlow: true,
                  addProfileForLoggedInUser: true,
                },
              });
            } else {
              shouldShowContactSupportModal = true;
            }
          }
        } catch (enrolledError) {
          shouldShowContactSupportModal = true;
          console.warn(
            "[ProfileUpdate] Enrolled programs call failed:",
            enrolledError,
          );
        }
      } else {
        try {
          const latestPd = useAuthStore.getState().profilesData;
          const uad = latestPd?.unified_account_details
            ?.user_account_details as
            | { grade?: { gradeId?: number } }
            | undefined;
          const plist = latestPd?.unified_account_details?.profiles ?? [];
          const defProf = plist.find((p) => p.is_default_profile) ?? plist[0];
          const gradeForEnroll = uad?.grade?.gradeId ?? 0;
          const examsFromProfile = (
            defProf as { exams?: Array<{ examId?: number }> } | undefined
          )?.exams;
          const examsParamSwitch =
            examsFromProfile
              ?.map((e) => e.examId)
              .filter((id): id is number => id != null)
              .join(",") ?? "";
          const enrolledSwitchResponse =
            (await studentsControllerGetEnrolledPrograms(
              {
                grade: gradeForEnroll,
                processEnrollment: true,
                ...(examsParamSwitch ? { exams: examsParamSwitch } : {}),
              },
              { clientName: "oneApp", headers: enrolledProgramsHeaders },
            )) as { data?: { programs?: EnrolledProgram[] } } | undefined;
          const programsSwitch = enrolledSwitchResponse?.data?.programs;
          if (programsSwitch?.length) {
            authState.setEnrolledPrograms(programsSwitch);
          }
        } catch (enrolledSwitchError) {
          console.warn(
            "[ProfileUpdate] Enrolled programs refresh after switch failed:",
            enrolledSwitchError,
          );
        }
      }

      try {
        await LiveclassStudent.create({ clientName: "liveclass" });
      } catch (syncError) {
        console.warn("[ProfileUpdate] Students sync call failed:", syncError);
      }

      /**
       * `generate-profile-token` often returns `access_token` without a new `refresh_token`.
       * Passing `undefined` would clear the persisted refresh token and break oneApp calls +
       * `refresh-profile-token` right after landing on the dashboard (401 cascade).
       */
      const previousRefresh = useAuthStore.getState().refreshToken;
      authState.setTokens(
        accessToken,
        refreshToken ?? previousRefresh ?? undefined,
      );

      await queryClient.cancelQueries();
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const first = query.queryKey[0];
          return (
            typeof first === "string" &&
            (first.startsWith("/students/") || first.startsWith("students"))
          );
        },
      });

      // const postAuth = useAuthStore.getState();
      // if (postAuth.accountsData) {
      //   authState.setAccountsData({
      //     ...postAuth.accountsData,
      //     access_token: accessToken,
      //     refresh_token:
      //       postAuth.refreshToken ?? postAuth.accountsData.refresh_token,
      //   });
      // }

      void queryClient.invalidateQueries({
        queryKey: ["aina-intro", "can-show-popup"],
      });

      try {
        const latest = useAuthStore.getState().profilesData;
        const uad = latest?.unified_account_details?.user_account_details;
        const profiles = latest?.unified_account_details?.profiles;
        const profilesForMarketing = profiles as
          | Array<{ vertical_id?: number; exams?: Array<{ name?: string }> }>
          | undefined;
        const ilProfile = profilesForMarketing?.find(
          (p) => p.vertical_id === 1,
        );
        const examName =
          (ilProfile?.exams?.length ?? 0 > 1)
            ? ilProfile?.exams
                ?.map((e) => e.name)
                .filter(Boolean)
                .join(",") || undefined
            : (ilProfile?.exams?.[0]?.name ?? undefined);
        const studentNameForMarketing =
          [uad?.first_name, uad?.last_name].filter(Boolean).join(" ").trim() ||
          undefined;
        const resGrade = uad?.grade as { gradeId?: number } | null | undefined;
        const gradeForMarketing = resGrade?.gradeId ?? payload.grade_id;

        const pageUrl =
          localStorage.getItem("web_deeplink_url") ?? window.location.href;

        if (!skipLsqMarketing) {
          emitPostAuthMarketingLsq({
            grade: gradeForMarketing,
            studentName: studentNameForMarketing,
            examName: examName as string | null | undefined,
            productVertical: ilProfile?.vertical_id?.toString(),
            academicGrade: resGrade?.gradeId ?? payload.grade_id,
            userEmail:
              typeof uad?.email === "string" ? uad.email.trim() : undefined,
            leadPageContext: { pageUrl },
            variant: "web",
          });
        }
      } catch (marketingError) {
        console.warn(
          "[ProfileUpdate] LSQ marketing event failed:",
          marketingError,
        );
      } finally {
        // Always reset syncing flag when profile update completes
        if (manageSyncing) {
          authState.setIsSyncing(false);
        }
      }

      return { shouldShowContactSupportModal, navigatedToSignupUpdateFlow };
    },
    [generateProfileTokenMutation],
  );

  return {
    profileUpdater,
    // showContactSupportModal,
    // setShowContactSupportModal,
  };
};

/** After username/password creation, refresh profile + session token. */
export async function refreshProfilesAndSession(options?: {
  accessToken?: string;
  refreshToken?: string;
}): Promise<void> {
  const authState = useAuthStore.getState();
  const currentToken = options?.accessToken ?? authState.token;
  const profilesData = authState.profilesData;

  if (!currentToken) {
    throw new Error("No authentication token found. Please login again.");
  }

  const unifiedAccountId =
    profilesData?.unified_account_details?.unified_account_id;
  const plist = profilesData?.unified_account_details?.profiles ?? [];
  const uamRaw =
    plist.find((p) => p.is_default_profile)?.uam_id ??
    plist.find((p) => p.vertical_id === 1)?.uam_id ??
    plist[0]?.uam_id;
  const uamId =
    uamRaw != null && Number.isFinite(Number(uamRaw)) && Number(uamRaw) > 0
      ? Number(uamRaw)
      : null;

  if (!unifiedAccountId || uamId == null) {
    throw new Error("No profile data found. Please login again.");
  }

  const profilesResponse = await magicMockApiV1UnifiedProfilesPost(
    { unified_account_id: unifiedAccountId } as unknown as Parameters<
      typeof magicMockApiV1UnifiedProfilesPost
    >[0],
    {
      clientName: "learnGateway",
      headers: getGatewayHeaders(`Bearer ${currentToken}`),
    },
  );

  const profilesDataTyped = profilesResponse as ProfilesData;
  const profilesAccessToken = profilesDataTyped.access_token ?? currentToken;
  authState.setProfilesData(profilesDataTyped);
  authState.setUnifiedAccountDetails(profilesDataTyped.unified_account_details);

  const genData =
    (await generateProfileTokenApiV1UnifiedGenerateProfileTokenPost(
      {
        unified_account_id: unifiedAccountId,
        uam_id: uamId,
      },
      undefined,
      {
        clientName: "learnGateway",
        headers: getGatewayHeaders(`Bearer ${profilesAccessToken}`),
      },
    )) as { access_token?: string; refresh_token?: string };

  if (!genData.access_token) {
    throw new Error("Failed to get access token");
  }

  try {
    await LiveclassStudent.create({ clientName: "liveclass" });
  } catch (syncError) {
    console.warn("[ProfileCredentials] Liveclass sync failed:", syncError);
  }

  const previousRefresh =
    options?.refreshToken ?? useAuthStore.getState().refreshToken;
  authState.setTokens(
    genData.access_token,
    genData.refresh_token ?? previousRefresh ?? undefined,
  );

  // const postAuth = useAuthStore.getState();
  // if (postAuth.accountsData) {
  //   authState.setAccountsData({
  //     ...postAuth.accountsData,
  //     access_token: genData.access_token,
  //     refresh_token:
  //       postAuth.refreshToken ?? postAuth.accountsData.refresh_token,
  //   });
  // }

  void queryClient.invalidateQueries({
    queryKey: ["aina-intro", "can-show-popup"],
  });
}
